/**
 * Client-side helpers for rendering "Hinzugefügt am … / NEU" badges on
 * ideas, scenes, reel-presets, and reel-templates.
 *
 * All items fetched from the API carry optional `createdAt` / `updatedAt` /
 * `releaseLabel` strings (ISO YYYY-MM-DD). These helpers translate those
 * into human-friendly German labels.
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const NEW_THRESHOLD_DAYS = 14;

/** German long-form date (e.g. "20. Apr 2026"). Safe against bad input. */
export function formatReleaseDate(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Relative age ("heute", "gestern", "vor 3 Tagen", "vor 2 Wochen", "vor 1 Monat", "vor 6 Monaten"). */
export function formatRelativeAge(
  iso: string | undefined | null,
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.floor((now.getTime() - d.getTime()) / MS_PER_DAY);
  if (days < 0) return formatReleaseDate(iso);
  if (days === 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 14) return "vor 1 Woche";
  if (days < 30) return `vor ${Math.floor(days / 7)} Wochen`;
  if (days < 60) return "vor 1 Monat";
  if (days < 365) return `vor ${Math.floor(days / 30)} Monaten`;
  return formatReleaseDate(iso);
}

export function isNewRelease(
  createdAt: string | undefined | null,
  thresholdDays: number = NEW_THRESHOLD_DAYS,
  now: Date = new Date(),
): boolean {
  if (!createdAt) return false;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  const days = Math.floor((now.getTime() - d.getTime()) / MS_PER_DAY);
  return days >= 0 && days <= thresholdDays;
}

/** Single consolidated descriptor for the UI — formatted strings plus flags. */
export type ReleaseBadge = {
  /** "20. Apr 2026" */
  dateLabel: string | null;
  /** "vor 3 Tagen" */
  relativeLabel: string | null;
  /** True when `createdAt` is within the NEW threshold. */
  isNew: boolean;
  /** Optional release bucket label, e.g. "Editorial Pack". */
  releaseLabel: string | null;
  /** Sortable timestamp (ms since epoch, 0 for unknown). */
  sortKey: number;
};

export function getReleaseBadge(input: {
  createdAt?: string;
  updatedAt?: string;
  releaseLabel?: string;
}): ReleaseBadge {
  const createdAt = input.createdAt ?? null;
  const dateLabel = formatReleaseDate(createdAt);
  const relativeLabel = formatRelativeAge(createdAt);
  const isNew = isNewRelease(createdAt);
  const releaseLabel = input.releaseLabel ?? null;
  const sortKey = createdAt
    ? (() => {
        const d = new Date(createdAt).getTime();
        return Number.isNaN(d) ? 0 : d;
      })()
    : 0;
  return { dateLabel, relativeLabel, isNew, releaseLabel, sortKey };
}
